import React, { useRef, useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

interface AvatarUploadProps {
  label: string;
  currentAvatarUrl?: string | null;
  onFileSelect: (file: File | null) => void;
  onRemove?: () => void;
  maxSizeMB?: number;
  size?: 'small' | 'medium' | 'large';
  shape?: 'circle' | 'square';
  error?: string;
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
}

const sizeClasses = {
  small: 'w-16 h-16',
  medium: 'w-24 h-24',
  large: 'w-32 h-32',
};

const iconSizeClasses = {
  small: 'w-6 h-6',
  medium: 'w-8 h-8',
  large: 'w-10 h-10',
};

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  label,
  currentAvatarUrl,
  onFileSelect,
  onRemove,
  maxSizeMB = 5,
  size = 'large',
  shape = 'circle',
  error,
  hint,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Use URL directly - API returns full URLs
  const fullAvatarUrl = currentAvatarUrl || null;

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const validateFile = (file: File): boolean => {
    // Check size
    if (file.size > maxSizeBytes) {
      setLocalError(t('businessProfile.fileTooLarge', { maxSize: maxSizeMB }));
      return false;
    }

    // Check type - only images
    if (!file.type.startsWith('image/')) {
      setLocalError(t('businessProfile.invalidImageType'));
      return false;
    }

    setLocalError(null);
    return true;
  };

  const handleFileSelect = (file: File | null) => {
    // Clean up previous preview
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }

    if (file && !validateFile(file)) {
      return;
    }

    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
    }

    onFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }

    setLocalError(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    onFileSelect(null);
    if (onRemove) {
      onRemove();
    }
  };

  const displayError = error || localError;
  const displayImage = preview || fullAvatarUrl;
  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  // Accessibility IDs
  const inputId = useId();
  const errorId = useId();
  const hintId = useId();
  const hasError = Boolean(displayError);
  const hasHint = Boolean(hint) && !hasError;

  return (
    <div className="space-y-3">
      <label htmlFor={inputId} className="block text-sm font-medium text-ink-primary">
        {label}
      </label>

      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
        {/* Avatar preview */}
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label={displayImage ? t('businessProfile.changePhoto') : t('businessProfile.uploadPhoto')}
          className={`relative ${sizeClasses[size]} ${shapeClass} overflow-hidden cursor-pointer group transition-all focus:outline-none focus:ring-2 focus:ring-brand-primary/50 ${
            displayError ? 'ring-2 ring-red-500' : ''
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {displayImage ? (
            <>
              <img
                src={displayImage}
                alt={label}
                className="w-full h-full object-cover"
              />
              {!disabled && (
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <svg
                    className={`${iconSizeClasses[size]} text-white`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              )}
            </>
          ) : (
            <div className={`w-full h-full bg-surface-card flex items-center justify-center ${
              !disabled ? 'group-hover:bg-surface-card-hover' : ''
            } transition-colors`}>
              {placeholder ? (
                <span className="text-2xl">{placeholder}</span>
              ) : (
                <svg
                  className={`${iconSizeClasses[size]} text-ink-muted`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Upload controls */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className="px-4 py-2 bg-surface-card border border-border-strong hover:bg-surface-card-hover text-ink-primary text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {displayImage
              ? t('businessProfile.changePhoto')
              : t('businessProfile.uploadPhoto')
            }
          </button>

          {displayImage && !disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="px-4 py-2 text-brand-warm-deep hover:text-brand-warm-deep text-sm font-medium transition-colors"
            >
              {t('businessProfile.removePhoto')}
            </button>
          )}

          <p className="text-xs text-ink-muted">
            JPG, PNG, GIF • {t('businessProfile.maxSize', { size: maxSizeMB })}
          </p>
        </div>

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          disabled={disabled}
          aria-describedby={hasError ? errorId : hasHint ? hintId : undefined}
          className="sr-only"
        />
      </div>

      {/* Hint */}
      {hasHint && (
        <p id={hintId} className="text-sm text-ink-muted">{hint}</p>
      )}

      {/* Error */}
      {hasError && (
        <p id={errorId} className="text-sm text-brand-warm-deep" role="alert">{displayError}</p>
      )}
    </div>
  );
};
